/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { When } from "react-if";
import slugify from "slugify";
import { Text, theme } from "@nordcloud/gnui";
import { KeyValue } from "~/generated/graphql";
import {
  DataDisplayWrapper,
  KeyValueList,
  NoData,
  BaselineDefinedSettings,
} from "~/components";
import { getTextFromStringify, isEmpty, isNotEmpty } from "~/tools";
import {
  getBaselineSourceIndex,
  getFlatPlanEntities,
  isPatchOrScanEntity,
} from "~/utils";
import {
  getPatchPolicy,
  getVisiblePatchPolicyParams,
  PatchPolicy,
  patchPolicyRules,
} from "~/utils/patchPolicyRules";
import {
  PlanField,
  SystemActionNames,
} from "~/views/plans/PlanCreate/components/PlanCreateWizard/constants";
import { usePlanWizard } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanProvider";
import { paramsExcludedFromBaselinePatching } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/constant";
import { usePlan } from "~/views/plans/PlanDetails/PlanProvider";
import { separateConnectedWords } from "~/views/plans/utils";

export function AdvancedSettings() {
  const { planAction } = usePlan();
  const { planData } = usePlanWizard();
  const parameters = planAction?.inputParameters?.filter(
    (item) => item.key !== SystemActionNames.DELAY
  );

  const patchPolicy = useMemo(() => getPatchPolicy(parameters), [parameters]);
  const visiblePatchPolicyParams = useMemo(
    () => getVisiblePatchPolicyParams(parameters, patchPolicy),
    [parameters, patchPolicy]
  );
  const parameterDescriptions = useMemo(
    () =>
      new Map(
        (planAction?.action?.parameterDefinitions ?? []).map((definition) => [
          definition.name,
          definition.description ?? "",
        ])
      ),
    [planAction?.action?.parameterDefinitions]
  );

  const flatPlanEntities = useMemo(
    () =>
      getFlatPlanEntities(
        planData?.[PlanField.PLAN_SETTINGS]?.planEntities ?? []
      ),
    [planData]
  );

  if (isEmpty(parameters ?? [])) {
    return <NoData hasIcon message="There are no Advanced Action Settings" />;
  }

  const baselineSourceIndex = getBaselineSourceIndex(flatPlanEntities);
  const planActionIndex =
    flatPlanEntities.findIndex((item) => item.id === planAction?.id) ?? -1;

  const isEntityBaselineDependent =
    baselineSourceIndex !== -1 &&
    baselineSourceIndex < planActionIndex &&
    isPatchOrScanEntity(planAction);

  return (
    <>
      <Text weight="medium">Advanced Action Settings</Text>
      <Text size="sm" tag="div" color={theme.color.text.text02}>
        Action Version
      </Text>
      <DataDisplayWrapper hasData={planAction?.action?.version !== null}>
        <Text tag="div" data-testid="div-action-version">
          {planAction?.action?.version}
        </Text>
        <Text size="sm" color={theme.color.text.text02}>
          {planAction?.action?.description}
        </Text>
      </DataDisplayWrapper>

      {parameters?.map((item: KeyValue) => {
        const isDefinedByPatchPolicy =
          patchPolicy &&
          patchPolicyRules[patchPolicy]?.defaultSettings &&
          item.key in patchPolicyRules[patchPolicy].defaultSettings;

        if (isDefinedByPatchPolicy) {
          return null;
        }

        const isHiddenByPatchPolicy =
          patchPolicy &&
          patchPolicyRules[patchPolicy]?.visibleParams &&
          !patchPolicyRules[patchPolicy].visibleParams?.includes(item.key);

        if (isHiddenByPatchPolicy) {
          return null;
        }

        const parameterDescription = parameterDescriptions.get(item.key) ?? "";

        return !isEntityBaselineDependent ||
          paramsExcludedFromBaselinePatching.includes(item.key) ? (
          <div css={{ marginBottom: theme.spacing.spacing03 }} key={item.key}>
            <When condition={isNotEmpty(getTextFromStringify(item.value))}>
              <Text size="sm" tag="div" color={theme.color.text.text02}>
                {separateConnectedWords(item.key)}
              </Text>
              <When condition={isNotEmpty(parameterDescription)}>
                <Text size="sm" color={theme.color.text.text02}>
                  {parameterDescription}
                </Text>
              </When>
              <Text
                data-testid={`text-${slugify(separateConnectedWords(item.key), {
                  lower: true,
                })}`}
              >
                {getTextFromStringify(item.value)}
              </Text>
            </When>
          </div>
        ) : null;
      })}
      <When
        condition={
          patchPolicy &&
          (patchPolicy === PatchPolicy.systemAll ||
            patchPolicy === PatchPolicy.securityOnly) &&
          !isEntityBaselineDependent
        }
      >
        <KeyValueList
          title="Settings defined by Patch Policy"
          items={
            !patchPolicy
              ? []
              : Object.entries(
                  patchPolicyRules[patchPolicy].defaultSettings
                ).map(([key, value]) => ({
                  key,
                  value,
                }))
          }
        />
      </When>
      <BaselineDefinedSettings
        isEntityBaselineDependent={isEntityBaselineDependent}
        parameters={
          visiblePatchPolicyParams.length > 0
            ? visiblePatchPolicyParams
            : parameters ?? []
        }
      />
    </>
  );
}
